import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Zap, Clock, PackageX, PackageCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const STATUS_OPTIONS = [
  { value: 'auto', label: 'Automático (por reloj)' },
  { value: 'proximo', label: 'Próximo' },
  { value: 'disponible', label: 'Disponible' },
  { value: 'finalizado', label: 'Finalizado' },
];

const emptyForm = {
  title: '',
  secondary_text: 'Próximo cupón',
  min_purchase: '',
  max_discount: '',
  available_date: '',
  available_time: '',
  affiliate_link: '',
  manual_status: 'auto',
  images: '',
  conditions_text: '',
};

const toDateTimeParts = (iso) => {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

const resolveStatus = (c) => {
  const now = Date.now();
  const avail = c.available_at ? new Date(c.available_at).getTime() : null;
  if (c.manual_status === 'finalizado') return 'Finalizado';
  if (c.manual_status === 'disponible') return 'Disponible';
  if (c.manual_status === 'proximo') return 'Próximo';
  return avail && now >= avail ? 'Disponible' : 'Próximo';
};

const UpcomingCouponsAdmin = ({ API, token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/upcoming-coupons`);
      setItems(res.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (c) => {
    const { date, time } = toDateTimeParts(c.available_at);
    setEditing(c);
    setForm({
      title: c.title || '',
      secondary_text: c.secondary_text || 'Próximo cupón',
      min_purchase: c.min_purchase ?? '',
      max_discount: c.max_discount ?? '',
      available_date: date,
      available_time: time,
      affiliate_link: c.affiliate_link || '',
      manual_status: c.manual_status || 'auto',
      images: Array.isArray(c.images) ? c.images.join('\n') : '',
      conditions_text: c.conditions_text || '',
    });
    setOpen(true);
  };

  const buildPayload = () => {
    let availableAt = null;
    if (form.available_date) {
      const t = form.available_time || '00:00';
      availableAt = new Date(`${form.available_date}T${t}:00`).toISOString();
    }
    const expiration = availableAt || new Date(Date.now() + 365 * 86400000).toISOString();
    return {
      title: form.title,
      description: form.secondary_text || '',
      code: '',
      expiration_date: expiration,
      min_purchase: form.min_purchase !== '' ? parseFloat(form.min_purchase) : null,
      max_discount: form.max_discount !== '' ? parseFloat(form.max_discount) : null,
      item_kind: 'upcoming',
      coupon_type: 'store',
      is_active: true,
      affiliate_link: form.affiliate_link || null,
      available_at: availableAt,
      secondary_text: form.secondary_text || 'Próximo cupón',
      manual_status: form.manual_status === 'auto' ? null : form.manual_status,
      images: form.images.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean).slice(0, 4),
      conditions_text: form.conditions_text || null,
    };
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Escribe el descuento (ej: 10% OFF)'); return; }
    if (form.manual_status === 'auto' && !form.available_date) {
      toast.error('Configura la fecha de disponibilidad (o fija un estado manual)');
      return;
    }
    try {
      const payload = buildPayload();
      const headers = { Authorization: `Bearer ${token}` };
      if (editing) {
        await axios.put(`${API}/coupons/${editing.id}`, payload, { headers });
        toast.success('Próximo cupón actualizado');
      } else {
        await axios.post(`${API}/coupons`, payload, { headers });
        toast.success('Próximo cupón creado');
      }
      setOpen(false);
      await fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este próximo cupón?')) return;
    try {
      await axios.delete(`${API}/coupons/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Eliminado');
      await fetchItems();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const toggleFinalized = async (c) => {
    const isFin = c.manual_status === 'finalizado';
    const payload = {
      title: c.title,
      description: c.description || '',
      code: c.code || '',
      expiration_date: c.expiration_date,
      min_purchase: c.min_purchase ?? null,
      max_discount: c.max_discount ?? null,
      item_kind: 'upcoming',
      coupon_type: c.coupon_type || 'store',
      is_active: true,
      affiliate_link: c.affiliate_link || null,
      available_at: c.available_at || null,
      secondary_text: c.secondary_text || null,
      manual_status: isFin ? null : 'finalizado',
      images: c.images || [],
      conditions_text: c.conditions_text || null,
    };
    try {
      await axios.put(`${API}/coupons/${c.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(isFin ? 'Reactivado' : 'Marcado como agotado');
      await fetchItems();
    } catch {
      toast.error('Error al actualizar');
    }
  };

  return (
    <div data-testid="upcoming-admin">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-[#1479FF]" />
          <h2 className="font-fredoka text-lg font-bold text-slate-900">Próximos cupones</h2>
        </div>
        <Button onClick={openNew} className="bg-[#1479FF] text-white hover:bg-[#0f63d6]" data-testid="add-upcoming-btn">
          <Plus className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Nuevo próximo cupón</span>
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-slate-400">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Zap className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No hay próximos cupones. Crea el primero.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((c) => {
              const st = resolveStatus(c);
              return (
                <div key={c.id} className="p-4 flex items-center justify-between gap-3" data-testid={`upcoming-row-${c.id}`}>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 truncate">{c.title}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {c.available_at ? new Date(c.available_at).toLocaleString('es-MX') : 'Sin fecha'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      st === 'Finalizado' ? 'bg-slate-100 text-slate-600'
                        : st === 'Disponible' ? 'bg-blue-100 text-blue-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {st}
                    </span>
                    <button
                      onClick={() => toggleFinalized(c)}
                      data-testid={`soldout-upcoming-${c.id}`}
                      title={c.manual_status === 'finalizado' ? 'Reactivar' : 'Marcar como agotado'}
                      className={`p-2 rounded-lg transition-colors ${c.manual_status === 'finalizado' ? 'text-emerald-600 hover:bg-emerald-50' : 'text-amber-600 hover:bg-amber-50'}`}
                    >
                      {c.manual_status === 'finalizado' ? <PackageCheck className="w-4 h-4" /> : <PackageX className="w-4 h-4" />}
                    </button>
                    <button onClick={() => openEdit(c)} className="p-2 text-slate-500 hover:text-[#1479FF] hover:bg-blue-50 rounded-lg" data-testid={`edit-upcoming-${c.id}`}>
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(c.id)} className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-upcoming-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl">
              {editing ? 'Editar próximo cupón' : 'Nuevo próximo cupón'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Descuento * (porcentaje o cantidad)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej: 10% OFF" data-testid="upcoming-form-title" />
            </div>
            <div className="space-y-1.5">
              <Label>Texto secundario</Label>
              <Input value={form.secondary_text} onChange={(e) => setForm({ ...form, secondary_text: e.target.value })} placeholder="Próximo cupón" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Compra mínima</Label>
                <Input type="number" value={form.min_purchase} onChange={(e) => setForm({ ...form, min_purchase: e.target.value })} placeholder="1500" />
              </div>
              <div className="space-y-1.5">
                <Label>Descuento máximo</Label>
                <Input type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} placeholder="300" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Fecha de disponibilidad</Label>
                <Input type="date" value={form.available_date} onChange={(e) => setForm({ ...form, available_date: e.target.value })} data-testid="upcoming-form-date" />
              </div>
              <div className="space-y-1.5">
                <Label>Hora exacta</Label>
                <Input type="time" value={form.available_time} onChange={(e) => setForm({ ...form, available_time: e.target.value })} data-testid="upcoming-form-time" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Link de afiliado / destino (opcional)</Label>
              <Input value={form.affiliate_link} onChange={(e) => setForm({ ...form, affiliate_link: e.target.value })} placeholder="Deja vacío para usar el link por defecto" />
            </div>
            <div className="space-y-1.5">
              <Label>Imágenes de productos (una URL por línea, máx. 4)</Label>
              <Textarea
                value={form.images}
                onChange={(e) => setForm({ ...form, images: e.target.value })}
                rows={3}
                placeholder={'https://...\nhttps://...'}
                data-testid="upcoming-form-images"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Estado</Label>
              <select
                value={form.manual_status}
                onChange={(e) => setForm({ ...form, manual_status: e.target.value })}
                data-testid="upcoming-form-status"
                className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
              >
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <Button onClick={handleSave} className="w-full bg-[#1479FF] text-white hover:bg-[#0f63d6]" data-testid="upcoming-form-save">
              {editing ? 'Guardar cambios' : 'Crear próximo cupón'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UpcomingCouponsAdmin;
